import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import {
  categoriaProfissaoLabels,
  categoriaProfissaoCores,
  tipoDocumentoLabels,
  tipoCampoLabels,
  type Profissao,
  type DocumentoProfissao,
  type CampoProfissao,
  type TipoCampo,
  type TreinamentoProfissao,
  type AbrangenciaTreinamento,
  type TipoDocumento,
} from '@/lib/profissoes';
import { useCatalogo, type Estado } from '@/lib/catalogo';
import { desativarProfissao, salvarProfissao } from '@/lib/profissoesRepo';
import CampoPeriodo from '@/components/base/CampoPeriodo';
import CampoAvisoVencimento from '@/components/base/CampoAvisoVencimento';
import { useJanelaDeAlerta } from '@/lib/notificacoes';
import { periodoEmDias, periodoTexto, type PeriodoParcial } from '@/lib/periodo';

// A vigência virou Período (migration 0047), e o rótulo passa a sair de
// `periodoTexto` — a mesma redação que o banco usa em `periodo_texto()`. A
// versão anterior adivinhava a unidade a partir dos dias e transformava
// "18 meses" em "1 ano".
const vigenciaLabel = (t: TreinamentoProfissao) =>
  periodoTexto({ valor: t.vigenciaValor, unidade: t.vigenciaUnidade });

const categoriaBadge = (cat: string) => {
  const cor = categoriaProfissaoCores[cat] || categoriaProfissaoCores.outros;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cor.bg} ${cor.text} ${cor.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cor.dot}`}></span>
      {categoriaProfissaoLabels[cat] || cat}
    </span>
  );
};

/* ==================== MODAL DE CADASTRO/EDIÇÃO ==================== */
function ProfissaoModal({
  profissao,
  onSave,
  onClose,
  salvando,
}: {
  profissao: Profissao | null;
  onSave: (p: Profissao) => void | Promise<void>;
  onClose: () => void;
  /** Gravar são várias idas ao banco; sem isto o botão parece não responder. */
  salvando: boolean;
}) {
  const isEditing = !!profissao;
  // Lista de estados vem de `estados_br` pelo catálogo (ADR-0011).
  const { itens: estadosBrasileiros } = useCatalogo<Estado>('estados');
  // Padrão global de antecedência, para o campo de aviso mostrar o que vale
  // quando ninguém preenche a exceção.
  const janelaDeTreinamento = useJanelaDeAlerta('treinamento');
  const [nome, setNome] = useState(profissao?.nome || '');
  const [descricao, setDescricao] = useState(profissao?.descricao || '');
  const [categoria, setCategoria] = useState(profissao?.categoria || 'manutencao');
  const [ativo, setAtivo] = useState(profissao?.ativo ?? true);
  const [observacoes, setObservacoes] = useState(profissao?.observacoes || '');
  // Registro profissional (secao 4.1): a profissão declara a exigência; o
  // documento é anexado na ficha do funcionário.
  const [requerRegistro, setRequerRegistro] = useState(
    profissao?.requerRegistroProfissional ?? false,
  );
  const [tipoRegistro, setTipoRegistro] = useState(profissao?.tipoRegistroProfissional || '');

  const [documentos, setDocumentos] = useState<DocumentoProfissao[]>(profissao?.documentos || []);
  const [campos, setCampos] = useState<CampoProfissao[]>(profissao?.campos || []);
  const [treinamentos, setTreinamentos] = useState<TreinamentoProfissao[]>(
    profissao?.treinamentos || [],
  );

  const [activeSection, setActiveSection] = useState<
    'info' | 'documentos' | 'campos' | 'treinamentos'
  >('info');

  const [novoDocNome, setNovoDocNome] = useState('');
  const [novoDocTipo, setNovoDocTipo] = useState<TipoDocumento>('documento');
  const [novoDocObrig, setNovoDocObrig] = useState(true);
  const [showDocForm, setShowDocForm] = useState(false);

  const [novoCampoNome, setNovoCampoNome] = useState('');
  const [novoCampoTipo, setNovoCampoTipo] = useState<TipoCampo>('text');
  const [novoCampoObrig, setNovoCampoObrig] = useState(true);
  const [novoCampoPlaceholder, setNovoCampoPlaceholder] = useState('');
  const [showCampoForm, setShowCampoForm] = useState(false);

  const [novoTreinoNome, setNovoTreinoNome] = useState('');
  const [novoTreinoNorma, setNovoTreinoNorma] = useState('');
  const [novoTreinoUrl, setNovoTreinoUrl] = useState('');
  const [novoTreinoAbrangencia, setNovoTreinoAbrangencia] =
    useState<AbrangenciaTreinamento>('nacional');
  const [novoTreinoEstados, setNovoTreinoEstados] = useState<string[]>([]);
  const [novoTreinoCidades, setNovoTreinoCidades] = useState('');
  // Vigência deixou de ser um select de dias fixos (migration 0047): é Período,
  // e a lista antiga não sabia dizer "18 meses" nem "45 dias".
  const [novoTreinoVigencia, setNovoTreinoVigencia] = useState<PeriodoParcial>({
    valor: 1,
    unidade: 'anos',
  });
  const [novoTreinoPrazo, setNovoTreinoPrazo] = useState<PeriodoParcial>({
    valor: null,
    unidade: null,
  });
  const [novoTreinoCargaHoraria, setNovoTreinoCargaHoraria] = useState('');
  const [novoTreinoObrig, setNovoTreinoObrig] = useState(true);
  const [novoTreinoQualquer, setNovoTreinoQualquer] = useState(false);
  const [novoTreinoAviso, setNovoTreinoAviso] = useState<number | null>(null);
  const [novoTreinoTempoMedio, setNovoTreinoTempoMedio] = useState<PeriodoParcial>({
    valor: null,
    unidade: null,
  });
  const [novoTreinoDesc, setNovoTreinoDesc] = useState('');
  const [showTreinoForm, setShowTreinoForm] = useState(false);

  const handleAddDoc = () => {
    if (!novoDocNome.trim()) return;
    setDocumentos([
      ...documentos,
      {
        id: `doc-${Date.now()}`,
        nome: novoDocNome.trim(),
        tipo: novoDocTipo,
        obrigatorio: novoDocObrig,
      },
    ]);
    setNovoDocNome('');
    setNovoDocTipo('documento');
    setNovoDocObrig(true);
    setShowDocForm(false);
  };

  const handleCancelDoc = () => {
    setNovoDocNome('');
    setNovoDocTipo('documento');
    setNovoDocObrig(true);
    setShowDocForm(false);
  };

  const handleRemoveDoc = (id: string) => setDocumentos(documentos.filter((d) => d.id !== id));

  const handleToggleDocObrig = (id: string) => {
    setDocumentos(documentos.map((d) => (d.id === id ? { ...d, obrigatorio: !d.obrigatorio } : d)));
  };

  const handleAddCampo = () => {
    if (!novoCampoNome.trim()) return;
    setCampos([
      ...campos,
      {
        id: `campo-${Date.now()}`,
        nome: novoCampoNome.trim(),
        tipo: novoCampoTipo,
        obrigatorio: novoCampoObrig,
        placeholder: novoCampoPlaceholder.trim() || undefined,
      },
    ]);
    setNovoCampoNome('');
    setNovoCampoTipo('text');
    setNovoCampoObrig(true);
    setNovoCampoPlaceholder('');
    setShowCampoForm(false);
  };

  const handleCancelCampo = () => {
    setNovoCampoNome('');
    setNovoCampoTipo('text');
    setNovoCampoObrig(true);
    setNovoCampoPlaceholder('');
    setShowCampoForm(false);
  };

  const handleRemoveCampo = (id: string) => setCampos(campos.filter((c) => c.id !== id));

  const handleToggleCampoObrig = (id: string) => {
    setCampos(campos.map((c) => (c.id === id ? { ...c, obrigatorio: !c.obrigatorio } : c)));
  };

  const handleAddTreino = () => {
    if (!novoTreinoNome.trim()) return;
    const cidadesList =
      novoTreinoAbrangencia === 'municipal' && novoTreinoCidades.trim()
        ? novoTreinoCidades
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean)
        : undefined;
    setTreinamentos([
      ...treinamentos,
      {
        id: `treino-${Date.now()}`,
        nome: novoTreinoNome.trim(),
        norma: novoTreinoNorma.trim() || undefined,
        urlLegislacao: novoTreinoUrl.trim() || undefined,
        abrangencia: novoTreinoAbrangencia,
        estados:
          novoTreinoAbrangencia === 'estadual' && novoTreinoEstados.length > 0
            ? novoTreinoEstados
            : undefined,
        cidades: cidadesList,
        vigenciaValor: novoTreinoVigencia.valor ?? 1,
        vigenciaUnidade: novoTreinoVigencia.unidade ?? 'anos',
        // Derivada pelo banco. Preenchida aqui só para o estado local somar; o
        // valor que vale volta da próxima leitura do catálogo.
        vigenciaDias: periodoEmDias(novoTreinoVigencia) ?? 365,
        prazoValor: novoTreinoPrazo.valor,
        prazoUnidade: novoTreinoPrazo.unidade,
        cargaHoraria: novoTreinoCargaHoraria.trim()
          ? Number(novoTreinoCargaHoraria.replace(',', '.'))
          : null,
        obrigatorio: novoTreinoObrig,
        descricao: novoTreinoDesc.trim() || undefined,
        aplicaAQualquerProfissao: novoTreinoQualquer,
        // A profissão aberta entra como destinatário no repositório, que é
        // quem sabe qual é. Aqui a lista nasce vazia de propósito.
        profissoes: [],
        diasAntecedenciaAviso: novoTreinoAviso,
        tempoMedioGeracaoValor: novoTreinoTempoMedio.valor,
        tempoMedioGeracaoUnidade: novoTreinoTempoMedio.unidade,
      },
    ]);
    limparFormularioDeTreinamento();
  };

  const limparFormularioDeTreinamento = () => {
    setNovoTreinoNome('');
    setNovoTreinoNorma('');
    setNovoTreinoUrl('');
    setNovoTreinoAbrangencia('nacional');
    setNovoTreinoEstados([]);
    setNovoTreinoCidades('');
    setNovoTreinoVigencia({ valor: 1, unidade: 'anos' });
    setNovoTreinoPrazo({ valor: null, unidade: null });
    setNovoTreinoCargaHoraria('');
    setNovoTreinoObrig(true);
    setNovoTreinoQualquer(false);
    setNovoTreinoAviso(null);
    setNovoTreinoTempoMedio({ valor: null, unidade: null });
    setNovoTreinoDesc('');
    setShowTreinoForm(false);
  };

  const handleCancelTreino = () => limparFormularioDeTreinamento();

  // Abrangência estadual costuma valer para o país inteiro: marcar as 27 uma a
  // uma é o caminho longo para o caso mais comum, e errar uma passa despercebido
  // no meio da grade.
  const todosOsEstados = estadosBrasileiros.map((e) => e.uf);
  const todosEstadosMarcados =
    estadosBrasileiros.length > 0 && novoTreinoEstados.length === estadosBrasileiros.length;

  const handleSelecionarTodosEstados = () => setNovoTreinoEstados(todosOsEstados);
  const handleLimparEstados = () => setNovoTreinoEstados([]);

  const handleRemoveTreino = (id: string) =>
    setTreinamentos(treinamentos.filter((t) => t.id !== id));

  const handleToggleTreinoObrig = (id: string) => {
    setTreinamentos(
      treinamentos.map((t) => (t.id === id ? { ...t, obrigatorio: !t.obrigatorio } : t)),
    );
  };

  const handleSave = () => {
    if (!nome.trim()) return;
    const novaProfissao: Profissao = {
      id: profissao?.id || `prof-${Date.now()}`,
      nome: nome.trim(),
      descricao: descricao.trim(),
      categoria,
      ativo,
      documentos,
      campos,
      treinamentos,
      observacoes: observacoes.trim() || undefined,
      requerRegistroProfissional: requerRegistro,
      tipoRegistroProfissional: requerRegistro ? tipoRegistro.trim() || null : null,
    };
    onSave(novaProfissao);
  };

  const sectionTabs = [
    { id: 'info' as const, label: 'Informações', icon: 'ri-information-line' },
    {
      id: 'documentos' as const,
      label: `Documentos (${documentos.length})`,
      icon: 'ri-file-list-line',
    },
    { id: 'campos' as const, label: `Campos (${campos.length})`, icon: 'ri-input-field' },
    {
      id: 'treinamentos' as const,
      label: `Treinamentos (${treinamentos.length})`,
      icon: 'ri-graduation-cap-line',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[5vh] px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-2xl shadow-lg flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center">
              <i className="ri-briefcase-line text-accent-600"></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground-900">
                {isEditing ? 'Editar Profissão' : 'Nova Profissão'}
              </h3>
              <p className="text-xs text-foreground-500">
                Configure documentos, campos e treinamentos esperados
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Section tabs */}
        <div className="px-5 pt-4 flex flex-wrap gap-1 shrink-0">
          {sectionTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                activeSection === tab.id
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100 border border-transparent'
              }`}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={`${tab.icon} text-xs`}></i>
              </div>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto">
          {activeSection === 'info' && (
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">
                  Nome da profissão *
                </label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Piscineiro"
                  className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  rows={3}
                  placeholder="Descreva as responsabilidades e atividades da profissão..."
                  className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm resize-none"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">
                    Categoria
                  </label>
                  <select
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value as Profissao['categoria'])}
                    className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                  >
                    {Object.entries(categoriaProfissaoLabels).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <button
                    onClick={() => setAtivo(!ativo)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      ativo ? 'bg-primary-500' : 'bg-background-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 rounded-full bg-background-50 transition-transform ${
                        ativo ? 'translate-x-4.5' : 'translate-x-0.5'
                      }`}
                    ></span>
                  </button>
                  <span
                    className={`text-sm font-medium ${ativo ? 'text-foreground-800' : 'text-foreground-400'}`}
                  >
                    {ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
              {/* Registro profissional (secao 4.1) */}
              <div className="rounded-lg border border-background-300/60 p-3">
                <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={requerRegistro}
                    onChange={(e) => {
                      setRequerRegistro(e.target.checked);
                      // Desmarcar limpa a sigla: a constraint do banco recusa
                      // sigla sem exigência, e guardar o valor de uma decisão
                      // antiga faz ele reaparecer sozinho depois.
                      if (!e.target.checked) setTipoRegistro('');
                    }}
                    className="w-4 h-4 mt-0.5 accent-primary-500"
                  />
                  <span className="text-sm text-foreground-700">
                    Exige registro em conselho de classe
                    <span className="block text-xs text-foreground-500">
                      A profissão só declara a exigência. O número, a validade e o arquivo são
                      anexados na ficha de cada funcionário.
                    </span>
                  </span>
                </label>

                {requerRegistro && (
                  <div className="mt-3">
                    <label
                      htmlFor="tipo-registro"
                      className="block text-xs font-medium text-foreground-700 mb-1"
                    >
                      Conselho / órgão<span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <input
                      id="tipo-registro"
                      type="text"
                      value={tipoRegistro}
                      onChange={(e) => setTipoRegistro(e.target.value)}
                      placeholder="Ex: CREA, CRM, OAB, MTE"
                      className="w-full sm:w-48 px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">
                  Observações internas
                </label>
                <textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  rows={2}
                  placeholder="Notas sobre horários, EPIs, regras específicas..."
                  className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm resize-none"
                />
              </div>
            </div>
          )}

          {activeSection === 'documentos' && (
            <div className="flex flex-col gap-4">
              {/* Botão para abrir formulário */}
              {!showDocForm ? (
                <button
                  onClick={() => setShowDocForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap self-start"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-add-line"></i>
                  </div>
                  Adicionar documento
                </button>
              ) : (
                <div className="bg-background-100/50 border border-background-200/70 rounded-lg p-4 flex flex-col gap-3">
                  <p className="text-xs font-semibold text-foreground-700">Novo documento</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={novoDocNome}
                      onChange={(e) => setNovoDocNome(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddDoc()}
                      placeholder="Nome do documento (ex: ASO, CREA...)"
                      className="flex-1 px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                      autoFocus
                    />
                    <select
                      value={novoDocTipo}
                      onChange={(e) => setNovoDocTipo(e.target.value as TipoDocumento)}
                      className="px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                    >
                      {Object.entries(tipoDocumentoLabels).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={novoDocObrig}
                        onChange={(e) => setNovoDocObrig(e.target.checked)}
                        className="w-4 h-4 accent-primary-500"
                      />
                      <span className="text-sm text-foreground-700">Obrigatório</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCancelDoc}
                        className="px-3 py-2 rounded-md border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleAddDoc}
                        disabled={!novoDocNome.trim()}
                        className="px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Confirmar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                {documentos.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-foreground-400">
                    Nenhum documento cadastrado. Adicione documentos esperados para esta profissão.
                  </div>
                ) : (
                  <div className="divide-y divide-background-200/50">
                    {documentos.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between px-4 py-3 group hover:bg-background-100/30"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-md bg-primary-50 flex items-center justify-center">
                            <i className="ri-file-line text-primary-600 text-xs"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-800">{doc.nome}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">
                                {tipoDocumentoLabels[doc.tipo]}
                              </span>
                              <span
                                className={`text-xs ${doc.obrigatorio ? 'text-red-600 font-medium' : 'text-foreground-400'}`}
                              >
                                {doc.obrigatorio ? 'Obrigatório' : 'Opcional'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleToggleDocObrig(doc.id)}
                            className="px-2 py-1 text-xs rounded-md border border-background-200/70 text-foreground-500 hover:text-foreground-700 hover:bg-background-100"
                            title="Alternar obrigatoriedade"
                          >
                            {doc.obrigatorio ? 'Tornar opcional' : 'Tornar obrigatório'}
                          </button>
                          <button
                            onClick={() => handleRemoveDoc(doc.id)}
                            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Remover"
                          >
                            <i className="ri-delete-bin-line text-xs"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-foreground-400">
                Total: {documentos.length} documento{documentos.length > 1 ? 's' : ''} ·{' '}
                {documentos.filter((d) => d.obrigatorio).length} obrigatório
                {documentos.filter((d) => d.obrigatorio).length > 1 ? 's' : ''}
              </p>
            </div>
          )}

          {activeSection === 'campos' && (
            <div className="flex flex-col gap-4">
              {/* Botão para abrir formulário */}
              {!showCampoForm ? (
                <button
                  onClick={() => setShowCampoForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap self-start"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-add-line"></i>
                  </div>
                  Adicionar campo
                </button>
              ) : (
                <div className="bg-background-100/50 border border-background-200/70 rounded-lg p-4 flex flex-col gap-3">
                  <p className="text-xs font-semibold text-foreground-700">Novo campo</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={novoCampoNome}
                      onChange={(e) => setNovoCampoNome(e.target.value)}
                      placeholder="Nome do campo (ex: Nome completo)"
                      className="flex-1 px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                      autoFocus
                    />
                    <select
                      value={novoCampoTipo}
                      onChange={(e) => setNovoCampoTipo(e.target.value as TipoCampo)}
                      className="px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                    >
                      {Object.entries(tipoCampoLabels).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    type="text"
                    value={novoCampoPlaceholder}
                    onChange={(e) => setNovoCampoPlaceholder(e.target.value)}
                    placeholder="Placeholder (dica do campo)"
                    className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={novoCampoObrig}
                        onChange={(e) => setNovoCampoObrig(e.target.checked)}
                        className="w-4 h-4 accent-primary-500"
                      />
                      <span className="text-sm text-foreground-700">Obrigatório</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCancelCampo}
                        className="px-3 py-2 rounded-md border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleAddCampo}
                        disabled={!novoCampoNome.trim()}
                        className="px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Confirmar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                {campos.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-foreground-400">
                    Nenhum campo cadastrado. Adicione os campos que devem ser preenchidos para esta
                    profissão.
                  </div>
                ) : (
                  <div className="divide-y divide-background-200/50">
                    {campos.map((campo) => (
                      <div
                        key={campo.id}
                        className="flex items-center justify-between px-4 py-3 group hover:bg-background-100/30"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-md bg-secondary-50 flex items-center justify-center">
                            <i className="ri-input-field text-secondary-600 text-xs"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-800">{campo.nome}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-50 text-secondary-700 border border-secondary-200">
                                {tipoCampoLabels[campo.tipo] || campo.tipo}
                              </span>
                              <span
                                className={`text-xs ${campo.obrigatorio ? 'text-red-600 font-medium' : 'text-foreground-400'}`}
                              >
                                {campo.obrigatorio ? 'Obrigatório' : 'Opcional'}
                              </span>
                              {campo.placeholder && (
                                <span className="text-xs text-foreground-400">
                                  "{campo.placeholder}"
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleToggleCampoObrig(campo.id)}
                            className="px-2 py-1 text-xs rounded-md border border-background-200/70 text-foreground-500 hover:text-foreground-700 hover:bg-background-100"
                          >
                            {campo.obrigatorio ? 'Tornar opcional' : 'Tornar obrigatório'}
                          </button>
                          <button
                            onClick={() => handleRemoveCampo(campo.id)}
                            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <i className="ri-delete-bin-line text-xs"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-foreground-400">
                Total: {campos.length} campo{campos.length > 1 ? 's' : ''} ·{' '}
                {campos.filter((c) => c.obrigatorio).length} obrigatório
                {campos.filter((c) => c.obrigatorio).length > 1 ? 's' : ''}
              </p>
            </div>
          )}

          {activeSection === 'treinamentos' && (
            <div className="flex flex-col gap-4">
              {/* Botão para abrir formulário */}
              {!showTreinoForm ? (
                <button
                  onClick={() => setShowTreinoForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap self-start"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-add-line"></i>
                  </div>
                  Adicionar treinamento
                </button>
              ) : (
                <div className="bg-background-100/50 border border-background-200/70 rounded-lg p-4 flex flex-col gap-3">
                  <p className="text-xs font-semibold text-foreground-700">Novo treinamento</p>
                  {/* Linha 1: Label + Norma */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={novoTreinoNome}
                      onChange={(e) => setNovoTreinoNome(e.target.value)}
                      placeholder="Label do treinamento (ex: NR-18 — Segurança no Trabalho)"
                      className="flex-1 px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                      autoFocus
                    />
                    <input
                      type="text"
                      value={novoTreinoNorma}
                      onChange={(e) => setNovoTreinoNorma(e.target.value)}
                      placeholder="Norma (ex: NR-18, Lei Lucas 13722)"
                      className="w-full sm:w-56 px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                    />
                  </div>

                  {/* Linha 2: URL Legislação */}
                  <input
                    type="url"
                    value={novoTreinoUrl}
                    onChange={(e) => setNovoTreinoUrl(e.target.value)}
                    placeholder="URL da legislação (ex: https://www.planalto.gov.br/...)"
                    className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                  />

                  {/* Linha 3: Abrangência + Vigência + Carga Horária + Obrigatório */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      value={novoTreinoAbrangencia}
                      onChange={(e) => {
                        setNovoTreinoAbrangencia(e.target.value as AbrangenciaTreinamento);
                        setNovoTreinoEstados([]);
                        setNovoTreinoCidades('');
                      }}
                      className="px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                    >
                      <option value="nacional">Nacional</option>
                      <option value="estadual">Estadual</option>
                      <option value="municipal">Municipal</option>
                    </select>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={novoTreinoCargaHoraria}
                      onChange={(e) =>
                        setNovoTreinoCargaHoraria(e.target.value.replace(/[^0-9.,]/g, ''))
                      }
                      placeholder="Carga horária (h)"
                      className="w-full sm:w-40 px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                    />
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={novoTreinoObrig}
                        onChange={(e) => setNovoTreinoObrig(e.target.checked)}
                        className="w-4 h-4 accent-primary-500"
                      />
                      <span className="text-sm text-foreground-700">Obrigatório</span>
                    </label>
                  </div>

                  {/* Vigência e prazo como Período (componente 3.1) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <CampoPeriodo
                      rotulo="Vigência do certificado"
                      valor={novoTreinoVigencia}
                      onChange={setNovoTreinoVigencia}
                      obrigatorio
                      ajuda="Conta a partir da conclusão do curso."
                    />
                    <CampoPeriodo
                      rotulo="Prazo para realizar"
                      valor={novoTreinoPrazo}
                      onChange={setNovoTreinoPrazo}
                      ajuda="Janela entre a exigência nascer e o curso ter de estar feito."
                      textoVazio="sem janela definida."
                    />
                  </div>

                  {/* Destinatário (secao 4.2) */}
                  <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={novoTreinoQualquer}
                      onChange={(e) => setNovoTreinoQualquer(e.target.checked)}
                      className="w-4 h-4 mt-0.5 accent-primary-500"
                    />
                    <span className="text-sm text-foreground-700">
                      Vale para qualquer profissão
                      <span className="block text-xs text-foreground-500">
                        Integração, uso de EPI, evacuação. Continua valendo para as profissões
                        cadastradas depois desta, sem ninguém precisar voltar aqui.
                      </span>
                    </span>
                  </label>

                  <CampoAvisoVencimento
                    diasAntecedencia={novoTreinoAviso}
                    onChangeDias={setNovoTreinoAviso}
                    tempoMedioGeracao={novoTreinoTempoMedio}
                    onChangeTempoMedio={setNovoTreinoTempoMedio}
                    diasPadraoGlobal={janelaDeTreinamento}
                  />

                  {/* Linha 4: Estados (condicional) */}
                  {novoTreinoAbrangencia === 'estadual' && (
                    <div className="bg-background-50 border border-background-200/70 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="text-xs font-medium text-foreground-700">
                          Selecionar estados:
                        </p>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={handleSelecionarTodosEstados}
                            disabled={todosEstadosMarcados}
                            className="px-2 py-1 rounded-md text-xs font-medium text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent whitespace-nowrap"
                          >
                            Selecionar todos
                          </button>
                          <span className="text-background-300" aria-hidden="true">
                            |
                          </span>
                          <button
                            type="button"
                            onClick={handleLimparEstados}
                            disabled={novoTreinoEstados.length === 0}
                            className="px-2 py-1 rounded-md text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent whitespace-nowrap"
                          >
                            Limpar todos
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {estadosBrasileiros.map((est) => {
                          const ativo = novoTreinoEstados.includes(est.uf);
                          return (
                            <button
                              key={est.uf}
                              type="button"
                              onClick={() => {
                                if (ativo) {
                                  setNovoTreinoEstados(
                                    novoTreinoEstados.filter((e) => e !== est.uf),
                                  );
                                } else {
                                  setNovoTreinoEstados([...novoTreinoEstados, est.uf]);
                                }
                              }}
                              className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors whitespace-nowrap cursor-pointer ${
                                ativo
                                  ? 'bg-primary-50 text-primary-700 border-primary-200'
                                  : 'bg-background-50 text-foreground-500 border-background-200/70 hover:border-primary-200 hover:text-primary-600'
                              }`}
                            >
                              {est.uf}
                            </button>
                          );
                        })}
                      </div>
                      {novoTreinoEstados.length > 0 && (
                        <p className="text-xs text-foreground-400 mt-2">
                          {novoTreinoEstados.length} estado{novoTreinoEstados.length > 1 ? 's' : ''}{' '}
                          selecionado{novoTreinoEstados.length > 1 ? 's' : ''}:{' '}
                          {novoTreinoEstados.join(', ')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Linha 4 alt: Cidades (condicional) */}
                  {novoTreinoAbrangencia === 'municipal' && (
                    <div>
                      <input
                        type="text"
                        value={novoTreinoCidades}
                        onChange={(e) => setNovoTreinoCidades(e.target.value)}
                        placeholder="Nomes das cidades (separados por vírgula)"
                        className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                      />
                      <p className="text-xs text-foreground-400 mt-1">
                        Separe os nomes das cidades por vírgula. Ex: São Paulo, Campinas, Santos
                      </p>
                    </div>
                  )}

                  {/* Linha 5: Descrição + Botões */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={novoTreinoDesc}
                      onChange={(e) => setNovoTreinoDesc(e.target.value)}
                      placeholder="Descrição do treinamento (opcional)"
                      className="flex-1 px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCancelTreino}
                        className="px-3 py-2 rounded-md border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleAddTreino}
                        disabled={!novoTreinoNome.trim()}
                        className="px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Confirmar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de treinamentos */}
              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                {treinamentos.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-foreground-400">
                    Nenhum treinamento cadastrado. Adicione treinamentos necessários para esta
                    profissão.
                  </div>
                ) : (
                  <div className="divide-y divide-background-200/50">
                    {treinamentos.map((treino) => (
                      <div key={treino.id} className="px-4 py-3 group hover:bg-background-100/30">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className="w-8 h-8 rounded-md bg-accent-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-graduation-cap-line text-accent-600 text-xs"></i>
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-foreground-800">
                                  {treino.nome}
                                </p>
                                {treino.norma && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-50 text-secondary-700 border border-secondary-200">
                                    {treino.norma}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className="text-xs px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">
                                  Validade: {vigenciaLabel(treino)}
                                </span>
                                {treino.cargaHoraria && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">
                                    {treino.cargaHoraria}
                                  </span>
                                )}
                                <span
                                  className={`text-xs font-medium ${treino.obrigatorio ? 'text-red-600' : 'text-foreground-400'}`}
                                >
                                  {treino.obrigatorio ? 'Obrigatório' : 'Opcional'}
                                </span>
                                <span
                                  className={`text-xs px-1.5 py-0.5 rounded ${
                                    treino.abrangencia === 'nacional'
                                      ? 'bg-green-50 text-green-700 border border-green-200'
                                      : treino.abrangencia === 'estadual'
                                        ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                                  }`}
                                >
                                  {treino.abrangencia === 'nacional'
                                    ? 'Nacional'
                                    : treino.abrangencia === 'estadual'
                                      ? 'Estadual'
                                      : 'Municipal'}
                                </span>
                              </div>
                              {treino.urlLegislacao && (
                                <a
                                  href={treino.urlLegislacao}
                                  target="_blank"
                                  rel="nofollow noopener noreferrer"
                                  className="inline-flex items-center gap-1 mt-1 text-xs text-primary-600 hover:text-primary-700 hover:underline"
                                >
                                  <i className="ri-external-link-line text-xs"></i>
                                  Legislação
                                </a>
                              )}
                              {treino.descricao && (
                                <p className="text-xs text-foreground-400 mt-1">
                                  {treino.descricao}
                                </p>
                              )}
                              {treino.estados && treino.estados.length > 0 && (
                                <p className="text-xs text-foreground-500 mt-0.5">
                                  Estados: {treino.estados.join(', ')}
                                </p>
                              )}
                              {treino.cidades && treino.cidades.length > 0 && (
                                <p className="text-xs text-foreground-500 mt-0.5">
                                  Cidades: {treino.cidades.join(', ')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleToggleTreinoObrig(treino.id)}
                              className="px-2 py-1 text-xs rounded-md border border-background-200/70 text-foreground-500 hover:text-foreground-700 hover:bg-background-100 whitespace-nowrap"
                            >
                              {treino.obrigatorio ? 'Tornar opcional' : 'Tornar obrigatório'}
                            </button>
                            <button
                              onClick={() => handleRemoveTreino(treino.id)}
                              className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <i className="ri-delete-bin-line text-xs"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-foreground-400">
                Total: {treinamentos.length} treinamento{treinamentos.length > 1 ? 's' : ''} ·{' '}
                {treinamentos.filter((t) => t.obrigatorio).length} obrigatório
                {treinamentos.filter((t) => t.obrigatorio).length > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-background-200/70 flex justify-end gap-2 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!nome.trim() || salvando}
            className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {salvando ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Criar profissão'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==================== MAIN PAGE ==================== */
export default function AdminProfissoesPage() {
  const { isSuperuser } = useAuth();
  // O catálogo vem do banco (migration 0022); antes a tela abria com um array
  // de profissões de mentira e só parecia carregada.
  const { itens: profissoesCatalogo, recarregar: recarregarCatalogo } =
    useCatalogo<Profissao>('profissoes');
  const [profissoes, setProfissoes] = useState<Profissao[]>([]);

  useEffect(() => {
    setProfissoes(profissoesCatalogo);
  }, [profissoesCatalogo]);
  const [busca, setBusca] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'ativo' | 'inativo'>('todos');
  const [filtroAbrangencia, setFiltroAbrangencia] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Profissao | null>(null);
  const [detailTarget, setDetailTarget] = useState<Profissao | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Profissao | null>(null);

  const filtradas = profissoes.filter((p) => {
    const matchBusca =
      p.nome.toLowerCase().includes(busca.toLowerCase()) ||
      p.descricao.toLowerCase().includes(busca.toLowerCase());
    const matchCat = filtroCategoria ? p.categoria === filtroCategoria : true;
    const matchStatus =
      filtroStatus === 'todos' ? true : filtroStatus === 'ativo' ? p.ativo : !p.ativo;
    const matchAbrangencia = filtroAbrangencia
      ? p.treinamentos.some((t) => t.abrangencia === filtroAbrangencia)
      : true;
    return matchBusca && matchCat && matchStatus && matchAbrangencia;
  });

  const total = profissoes.length;
  const ativos = profissoes.filter((p) => p.ativo).length;
  const inativos = profissoes.filter((p) => !p.ativo).length;
  const docsTotal = profissoes.reduce((s, p) => s + (p.documentos?.length ?? 0), 0);
  const treinosTotal = profissoes.reduce((s, p) => s + (p.treinamentos?.length ?? 0), 0);

  const [salvando, setSalvando] = useState(false);
  const [erroPersistencia, setErroPersistencia] = useState<string | null>(null);

  const handleSave = async (p: Profissao) => {
    setErroPersistencia(null);
    setSalvando(true);

    try {
      await salvarProfissao(p);
    } catch (e) {
      // Sem gravação não há o que refletir na tela: manter o modal aberto com o
      // erro é melhor que fechar e deixar a pessoa achar que salvou.
      setErroPersistencia(e instanceof Error ? e.message : 'Erro ao salvar a profissão.');
      setSalvando(false);
      return;
    }

    // Recarrega do servidor em vez de espelhar o que a tela achava que salvou:
    // a gravação normaliza ids e ordena os filhos, e outra pessoa pode ter
    // mexido no mesmo catálogo enquanto o modal estava aberto.
    recarregarCatalogo();

    setSalvando(false);
    setModalOpen(false);
    setEditTarget(null);
  };

  const handleEdit = (p: Profissao) => {
    setEditTarget(p);
    setModalOpen(true);
  };

  const handleDelete = async (p: Profissao) => {
    setErroPersistencia(null);

    try {
      // Desativa, não apaga: funcionário cadastrado guarda o id da profissão e
      // treinamento concluído aponta para o catálogo.
      await desativarProfissao(p.id);
    } catch (e) {
      setErroPersistencia(e instanceof Error ? e.message : 'Erro ao desativar a profissão.');
      setConfirmDelete(null);
      return;
    }

    setProfissoes((atuais) => atuais.map((pr) => (pr.id === p.id ? { ...pr, ativo: false } : pr)));
    setConfirmDelete(null);
  };

  if (!isSuperuser) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Esta área é reservada para o superusuário administrador da plataforma.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Link
                to="/admin"
                className="text-sm text-foreground-500 hover:text-foreground-700 flex items-center gap-1"
              >
                <i className="ri-arrow-left-line"></i>
                Administração
              </Link>
              <span className="text-foreground-300">/</span>
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900">Profissões</h1>
            <p className="text-sm text-foreground-500 mt-0.5">
              Cadastre profissões, defina documentos esperados, campos obrigatórios e treinamentos
              com vigência.
            </p>
          </div>
          <button
            onClick={() => {
              setEditTarget(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </div>
            Nova profissão
          </button>
        </div>

        {/* Cards resumo */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            {
              label: 'Total cadastradas',
              valor: total,
              icone: 'ri-briefcase-line',
              corIcon: 'text-primary-600',
              corBg: 'bg-primary-50',
            },
            {
              label: 'Ativas',
              valor: ativos,
              icone: 'ri-check-double-line',
              corIcon: 'text-green-600',
              corBg: 'bg-green-50',
            },
            {
              label: 'Documentos esperados',
              valor: docsTotal,
              icone: 'ri-file-list-line',
              corIcon: 'text-accent-600',
              corBg: 'bg-accent-50',
            },
            {
              label: 'Treinamentos vinculados',
              valor: treinosTotal,
              icone: 'ri-graduation-cap-line',
              corIcon: 'text-secondary-700',
              corBg: 'bg-secondary-50',
            },
          ].map((card) => (
            <div
              key={card.label}
              className="bg-background-50 border border-background-200/70 rounded-lg p-4"
            >
              <div className="flex items-center gap-2 mb-1">
                <div
                  className={`w-8 h-8 rounded-lg ${card.corBg} flex items-center justify-center`}
                >
                  <i className={`${card.icone} ${card.corIcon} text-sm`}></i>
                </div>
                <p className="text-xs font-medium text-foreground-500">{card.label}</p>
              </div>
              <p className="text-2xl font-bold text-foreground-900">{card.valor}</p>
            </div>
          ))}
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
            </div>
            <input
              type="text"
              placeholder="Buscar profissão..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            />
          </div>
          <select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
            className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
          >
            <option value="">Todas as categorias</option>
            {Object.entries(categoriaProfissaoLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value as typeof filtroStatus)}
            className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
          >
            <option value="todos">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>
          <select
            value={filtroAbrangencia}
            onChange={(e) => setFiltroAbrangencia(e.target.value)}
            className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
          >
            <option value="">Todas as abrangências</option>
            <option value="nacional">Nacional</option>
            <option value="estadual">Estadual</option>
            <option value="municipal">Municipal</option>
          </select>
        </div>

        {/* Tabela */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Profissão
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Categoria
                  </th>
                  <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Documentos
                  </th>
                  <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Campos
                  </th>
                  <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Treinamentos
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Status
                  </th>
                  <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/50">
                {filtradas.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-foreground-400">
                      Nenhuma profissão encontrada.
                    </td>
                  </tr>
                ) : (
                  filtradas.map((p) => (
                    <tr key={p.id} className="hover:bg-background-100/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              p.ativo ? 'bg-accent-50' : 'bg-background-100'
                            }`}
                          >
                            <i
                              className={`ri-briefcase-line ${p.ativo ? 'text-accent-600' : 'text-foreground-400'} text-sm`}
                            ></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-800 truncate">
                              {p.nome}
                            </p>
                            <p className="text-xs text-foreground-500 truncate max-w-[240px]">
                              {p.descricao}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">{categoriaBadge(p.categoria)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-semibold text-foreground-800">
                          {p.documentos.length}
                        </span>
                        <p className="text-xs text-foreground-400">
                          {p.documentos.filter((d) => d.obrigatorio).length} obrig.
                        </p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-semibold text-foreground-800">
                          {p.campos.length}
                        </span>
                        <p className="text-xs text-foreground-400">
                          {p.campos.filter((c) => c.obrigatorio).length} obrig.
                        </p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-semibold text-foreground-800">
                          {p.treinamentos.length}
                        </span>
                        <div className="flex items-center justify-center gap-1 mt-0.5">
                          {(() => {
                            const abrs = [...new Set(p.treinamentos.map((t) => t.abrangencia))];
                            return abrs.map((ab) => (
                              <span
                                key={ab}
                                className={`inline-block w-2 h-2 rounded-full ${
                                  ab === 'nacional'
                                    ? 'bg-green-500'
                                    : ab === 'estadual'
                                      ? 'bg-amber-500'
                                      : 'bg-blue-500'
                                }`}
                                title={
                                  ab === 'nacional'
                                    ? 'Nacional'
                                    : ab === 'estadual'
                                      ? 'Estadual'
                                      : 'Municipal'
                                }
                              ></span>
                            ));
                          })()}
                        </div>
                        <p className="text-xs text-foreground-400 mt-0.5">
                          {p.treinamentos.filter((t) => t.obrigatorio).length} obrig.
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            p.ativo
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-secondary-100 text-secondary-900 border-secondary-200'
                          }`}
                        >
                          {p.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setDetailTarget(p)}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors"
                            title="Ver detalhes"
                          >
                            <i className="ri-eye-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => handleEdit(p)}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-accent-600 transition-colors"
                            title="Editar"
                          >
                            <i className="ri-pencil-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => setConfirmDelete(p)}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-red-600 transition-colors"
                            title="Excluir"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal cadastro/edição */}
      {erroPersistencia && (
        <div className="fixed bottom-6 right-6 z-[60] max-w-sm px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 shadow-lg">
          {erroPersistencia}
        </div>
      )}

      {modalOpen && (
        <ProfissaoModal
          profissao={editTarget}
          onSave={handleSave}
          salvando={salvando}
          onClose={() => {
            setModalOpen(false);
            setEditTarget(null);
          }}
        />
      )}

      {/* Modal detalhe */}
      {detailTarget && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDetailTarget(null)}></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-lg shadow-lg max-h-[80vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${detailTarget.ativo ? 'bg-accent-50' : 'bg-background-100'}`}
                >
                  <i
                    className={`ri-briefcase-line ${detailTarget.ativo ? 'text-accent-600' : 'text-foreground-400'}`}
                  ></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">{detailTarget.nome}</h3>
                  <p className="text-xs text-foreground-500">
                    {categoriaProfissaoLabels[detailTarget.categoria]}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDetailTarget(null)}
                className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-5 flex flex-col gap-5">
              <div>
                <p className="text-xs text-foreground-500 mb-1">Descrição</p>
                <p className="text-sm text-foreground-700">{detailTarget.descricao}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded bg-primary-50 flex items-center justify-center">
                      <i className="ri-file-list-line text-primary-600 text-xs"></i>
                    </div>
                    <p className="text-xs font-medium text-foreground-500">Documentos</p>
                  </div>
                  <p className="text-lg font-bold text-foreground-900">
                    {detailTarget.documentos.length}
                  </p>
                  <p className="text-xs text-foreground-400">
                    {detailTarget.documentos.filter((d) => d.obrigatorio).length} obrigatórios
                  </p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded bg-accent-50 flex items-center justify-center">
                      <i className="ri-graduation-cap-line text-accent-600 text-xs"></i>
                    </div>
                    <p className="text-xs font-medium text-foreground-500">Treinamentos</p>
                  </div>
                  <p className="text-lg font-bold text-foreground-900">
                    {detailTarget.treinamentos.length}
                  </p>
                  <p className="text-xs text-foreground-400">
                    {detailTarget.treinamentos.filter((t) => t.obrigatorio).length} obrigatórios
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-foreground-700 mb-2">
                  Documentos esperados
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {detailTarget.documentos.map((d) => (
                    <span
                      key={d.id}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs border ${
                        d.obrigatorio
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : 'bg-background-100 text-foreground-500 border-background-200'
                      }`}
                    >
                      <i
                        className={`${d.obrigatorio ? 'ri-file-warning-line' : 'ri-file-line'} text-xs`}
                      ></i>
                      {d.nome}
                      <span className="opacity-60">({tipoDocumentoLabels[d.tipo]})</span>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-foreground-700 mb-2">
                  Campos obrigatórios
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {detailTarget.campos
                    .filter((c) => c.obrigatorio)
                    .map((c) => (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-secondary-50 text-secondary-700 border border-secondary-200"
                      >
                        {c.nome}
                      </span>
                    ))}
                  {detailTarget.campos.filter((c) => c.obrigatorio).length === 0 && (
                    <p className="text-xs text-foreground-400">Nenhum campo obrigatório definido</p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-foreground-700 mb-2">
                  Treinamentos necessários
                </p>
                <div className="flex flex-col gap-2">
                  {detailTarget.treinamentos.map((t) => (
                    <div key={t.id} className="px-3 py-2.5 rounded-md bg-background-100/50">
                      <div className="flex items-center gap-2 mb-1">
                        <i
                          className={`${t.obrigatorio ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-400'} text-xs`}
                        ></i>
                        <span className="text-sm font-medium text-foreground-700">{t.nome}</span>
                        {t.norma && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-50 text-secondary-700 border border-secondary-200">
                            {t.norma}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap text-xs text-foreground-500">
                        <span className="px-1.5 py-0.5 rounded bg-background-100">
                          Validade: {vigenciaLabel(t)}
                        </span>
                        {t.cargaHoraria && (
                          <span className="px-1.5 py-0.5 rounded bg-background-100">
                            {t.cargaHoraria}
                          </span>
                        )}
                        <span
                          className={`font-medium ${t.obrigatorio ? 'text-red-600' : 'text-foreground-400'}`}
                        >
                          {t.obrigatorio ? 'Obrigatório' : 'Opcional'}
                        </span>
                        <span
                          className={`px-1.5 py-0.5 rounded ${
                            t.abrangencia === 'nacional'
                              ? 'bg-green-50 text-green-700 border border-green-200'
                              : t.abrangencia === 'estadual'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                          }`}
                        >
                          {t.abrangencia === 'nacional'
                            ? 'Nacional'
                            : t.abrangencia === 'estadual'
                              ? 'Estadual'
                              : 'Municipal'}
                        </span>
                      </div>
                      {t.urlLegislacao && (
                        <a
                          href={t.urlLegislacao}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-1 text-xs text-primary-600 hover:text-primary-700 hover:underline"
                        >
                          <i className="ri-external-link-line text-xs"></i>
                          Legislação
                        </a>
                      )}
                      {t.descricao && (
                        <p className="text-xs text-foreground-400 mt-1">{t.descricao}</p>
                      )}
                      {t.estados && t.estados.length > 0 && (
                        <p className="text-xs text-foreground-500 mt-0.5">
                          Estados: {t.estados.join(', ')}
                        </p>
                      )}
                      {t.cidades && t.cidades.length > 0 && (
                        <p className="text-xs text-foreground-500 mt-0.5">
                          Cidades: {t.cidades.join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {detailTarget.observacoes && (
                <div className="bg-background-100/50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-foreground-700 mb-1">Observações</p>
                  <p className="text-sm text-foreground-600">{detailTarget.observacoes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirma exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setConfirmDelete(null)}
          ></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-sm shadow-lg p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-alert-line text-red-600"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">Excluir profissão</h3>
                <p className="text-xs text-foreground-500">
                  Tem certeza que deseja excluir "{confirmDelete.nome}"?
                </p>
              </div>
            </div>
            <p className="text-sm text-foreground-600 mb-5">
              Esta ação não pode ser desfeita. Todos os documentos, campos e treinamentos vinculados
              serão perdidos.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-md border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="px-4 py-2 rounded-md bg-red-600 text-background-50 text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
